import { Contractor as ContractorModel, ContractorCreatePayload } from '@/models/Contractor';
import { Component, Vue } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { AxiosError } from 'axios';

import { openDialog } from '@/support/Dialog';
import { object as dotObject } from 'dot-object';
import { MiFormField } from '@/components/MiForm/MiForm';
import { merge } from 'lodash';
import { getStatusColor, getStatusLabel } from '@/support/ReportStatus';
import { PusherService } from '@/support/PusherService';
import { Application } from '@/support/Application';
import { ContractorSyncFailed, ContractorSyncFinished, ContractorSyncStarted } from '@/support/Pusher/ContractorSync';

@Component<Contractor>({})
export default class Contractor extends Vue {
  protected $pageTitle = 'Herstelbedrijf';

  protected activeTab = 'planning';

  protected currentPath = '';

  protected isFormValid = true;

  protected editedFormFields: MiFormField[] = [];

  protected contractor: ContractorModel | null = null;

  protected isLoading = true;

  protected isSyncing = false;

  protected isEditing = false;

  protected async mounted(): Promise<void> {
    this.setActiveTab();
    await this.initialize();
    this.initializePusher();
    this.isLoading = false;
  }

  protected async initialize(): Promise<void> {
    await this.fetchReport();
  }

  protected async fetchReport(): Promise<void> {
    if (! this.$route.params.id) {
      return;
    }

    this.isLoading = true;
    this.contractor = null;

    await new ContractorModel()
      .include(['review', 'user', 'address'])
      .find(this.$route.params.id)
      .then((contractor: ContractorModel) => {
        this.contractor = contractor;
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected toggleEdit(): void {
    if (! this.isEditing) {
      this.isEditing = true;
      return;
    }

    openDialog({
      title: 'Bewerken annuleren',
      text: 'Weet je zeker dat je het bewerken wilt annuleren? Wijzigingen zullen niet worden opgeslagen',
      type: 'warning',
      buttons: {
        cancel: { color: 'gray', text: 'Nee' },
        confirm: { color: 'error',
          text: 'Ja',
          action: () => {
            this.cancelEditing();
          } },
      },
    });
  }

  protected setActiveTab(): void {
    const urlParts = window.location.pathname.split('/');
    this.currentPath = `/${urlParts[1]}/${urlParts[2]}`;

    if (! urlParts[3]) {
      this.$router.replace(`${this.currentPath}/planning`);
    } else {
      this.activeTab = window.location.pathname;
    }
  }

  protected cancelEditing(): void {
    this.isEditing = false;
  }

  protected getStatusColor(key: string): string {
    return getStatusColor(key);
  }

  protected getPillColor(status: string): string {
    return getStatusColor(status);
  }

  protected getPillLabel(status: string): string {
    return getStatusLabel(status);
  }

  protected handleFieldChanged(field: MiFormField): void {
    let replacedValue = false;
    this.editedFormFields = this.editedFormFields.map((formField: MiFormField): MiFormField => {
      if (formField.key !== field.key) {
        return formField;
      }
      replacedValue = true;
      return { ...formField, ...{ value: field.value } };
    });
    if (replacedValue) {
      return;
    }
    this.editedFormFields.push(field);
  }

  protected async update(): Promise<void> {
    if (! this.isFormValid || this.isLoading) {
      return;
    }
    this.isLoading = true;
    const payload: ContractorCreatePayload = this.createUpdatePayload();
    const contractor: ContractorModel | null = await new ContractorModel()
      .updateContractor(
        this.$route.params.id,
        payload,
      )
      .catch((): null => null);
    if (! contractor) {
      return;
    }
    this.isLoading = false;

    this.contractor = merge(this.contractor, payload);

    this.cancelEditing();
    this.initialize();
  }

  protected createUpdatePayload(): ContractorCreatePayload {
    return this.editedFormFields.reduce(
      (previousValue: ContractorCreatePayload, field: MiFormField): ContractorCreatePayload => merge(
        previousValue,
        dotObject(
          {
            [field.key]: field.value,
          },
        ),
      ),
      {} as ContractorCreatePayload,
    );
  }

  protected get companyFields(): MiFormField[] {
    if (! this.contractor) {
      return [];
    }

    return [
      {
        type: 'text',
        key: 'address.street',
        placeholder: 'Straat',
        value: this.contractor.address ? this.contractor.address.street : '',
        label: 'Straat',
        rules: ['required'],
      },
      {
        type: 'number',
        key: 'address.number',
        placeholder: 'Huisnummer',
        value: this.contractor.address ? this.contractor.address.number : '',
        label: 'Huisnumer',
        class: 'w-1/2',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'address.number_add',
        placeholder: 'Toevoeging',
        value: this.contractor.address ? this.contractor.address.number_add : '',
        label: 'Toevoeging',
        class: 'w-1/2',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'address.city',
        placeholder: 'Stad',
        value: this.contractor.address ? this.contractor.address.city : '',
        label: 'Stad',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'address.postcode',
        placeholder: 'Postcode',
        value: this.contractor.address ? this.contractor.address.postcode : '',
        label: 'Postcode',
        rules: ['required', 'dutchPostcode'],
      },
      {
        type: 'text',
        key: 'contact_name',
        placeholder: 'Contactpersoon',
        value: this.contractor.contact_name ? this.contractor.contact_name : '',
        label: 'Contactpersoon',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'contact_function',
        placeholder: 'Functie contactpersoon',
        value: this.contractor.contact_function ? this.contractor.contact_function : '',
        label: 'Functie contactpersoon',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'site',
        placeholder: 'Website',
        value: this.contractor.site ? this.contractor.site : '',
        label: 'Website',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'contact_email',
        placeholder: 'Email',
        value: this.contractor.contact_email ? this.contractor.contact_email : '',
        label: 'Email',
        rules: ['required', 'email'],
      },
      {
        type: 'text',
        key: 'phone',
        placeholder: 'Telefoonnummer',
        value: this.contractor.phone ? this.contractor.phone : '',
        label: 'Telefoonnummer',
        rules: ['required', 'dutchPhone'],
      },
    ];
  }

  protected get tabs(): Tab[] {
    return [
      {
        name: 'Planning',
        key: 'planning',
        href: '/planning',
        visible: () => true,
      },
      {
        name: 'Feedback',
        key: 'feedback',
        href: '/feedback',
        visible: () => true,
      },
      {
        name: 'Gebruikers',
        key: 'users',
        href: '/users',
        visible: () => true,
      },
    ];
  }

  protected get infoFields(): MiFormField[] {
    if (! this.contractor) {
      return [];
    }

    return [
      {
        type: 'text',
        key: 'coc',
        placeholder: 'KVK-nummer',
        value: this.contractor.coc,
        label: 'KVK-nummer',
        rules: ['required'],
      },
      {
        type: 'date',
        key: 'registered_since',
        placeholder: 'Ingeschreven bij de KVK sinds',
        value: this.contractor.registered_since,
        label: 'Ingeschreven bij de KVK sinds',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'vat_number',
        placeholder: 'BTW-nummer',
        value: this.contractor.vat_number,
        label: 'BTW-nummer',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'purchase_order_number',
        placeholder: 'Inkoopordernummer',
        value: this.contractor.purchase_order_number,
        label: 'Inkoopordernummer',
        rules: ['required'],
      },
    ];
  }

  // PUSHER
  /**
   * Initalize Pusher room and listen for events
   */
   private pusher: PusherService | null = null;

   private initializePusher() {
     if (! Application.config('APP_APPLICATION_MODE') || ! this.$store.state.Auth) {
       return;
     }

     this.pusher = (new PusherService() as PusherService)
       .author(this.$store.state.Auth)
       .room(Application.config('APP_APPLICATION_MODE') as string)
       .echo()
       .on('contractor:sync.started', (event: ContractorSyncStarted) => {
         this.handleSyncStarted(event.contractor);
       })
       .on('contractor:sync.finished', (event: ContractorSyncFinished) => {
         this.handleSyncFinished(event.contractor);
       })
       .on('contractor:sync.failed', (event: ContractorSyncFailed) => {
         this.handleSyncFailed(event.contractor, event.error);
       });
   }

   private handleSyncStarted(syncContractor: ContractorModel) {
     if (this.contractor && syncContractor.uuid !== this.contractor.uuid) {
       return;
     }

     this.isSyncing = true;
     this.$root.$emit('syncContractor', true);
   }

   private handleSyncFinished(syncContractor: ContractorModel) {
     if (this.contractor && syncContractor.uuid !== this.contractor.uuid) {
       return;
     }

     this.isSyncing = false;
     this.$root.$emit('syncContractor', false);
   }

   private handleSyncFailed(syncContractor: ContractorModel, error: string) {
     if (this.contractor && ((syncContractor.uuid !== this.contractor.uuid) || ! error)) {
       return;
     }

     this.isSyncing = false;
     this.$root.$emit('syncContractor', false);
   }
}

interface Tab {
  name: string;
  key: string;
  href: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  visible: Function;
}
